import { fetchEventSource } from "@microsoft/fetch-event-source";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { components } from "../../apis/gen";
import {
	create_append_sessions_claim,
	create_checkin_claim,
	get_agents,
} from "../../apis/helper";
import type { UpdateEvents as SseData } from "../../types/event-updates";
import { kioskQueuePlayback, proxyKioskUpdate } from "../kiosk/kiosk-action";
import type { AppDispatch, ThunkAPI, ThunkConfig } from "../store";
import {
	addSessionHighlight,
	addSessionResult,
	eventBooked,
	eventCheckedIn,
	sessionArchived,
	sessionProcessingDone,
	sessionSharelinkReady,
	sessionStreamReady,
	updateSessionHighlight,
} from "./events-action";
import { fetchEventHandler } from "./events-handlers";
import { EventActionTypes, type ResultDetailExtended } from "./events-types";

export const fetchEventThunk = createAsyncThunk(
	EventActionTypes.FETCH_EVENT,
	fetchEventHandler,
);

const initSse = async (eventId: string, thunkAPI: ThunkAPI) => {
	const dispatch = thunkAPI.dispatch as AppDispatch;
	const sseUrl = `${import.meta.env.VITE_APP_API_URL}/events/${eventId}/monitor`;

	await fetchEventSource(sseUrl, {
		headers: {
			Authorization: `Bearer ${import.meta.env.VITE_APP_OWNER_AUTH}`,
		},
		credentials: "include",
		signal: thunkAPI.signal,
		keepalive: true,
		openWhenHidden: true,
		onmessage: (event) => {
			if (event.data === ":heartbeat" || !event.data) return;

			try {
				const data: SseData = JSON.parse(event.data);
				console.log(
					"📨 SSE message received:",
					data.EventUpdate,
					data.SessionUpdate,
					data,
				);

				// Dispatch to Kiosk slice for state management. This allows the kiosk UI
				// to react to events that might not directly alter the event data structure,
				// such as game state transitions used for presentation logic.
				dispatch(proxyKioskUpdate(data));

				if ("EventUpdate" in data) {
					const e = data.EventUpdate;

					// Handle major state transitions by re-fetching the entire event object.
					// This is the safest way to ensure the local state is perfectly in sync.
					if (
						typeof e === "string" || // "started", "finished", "cancelled"
						"session_appended" in e ||
						"session_ready" in e ||
						"new_participants" in e
					) {
						console.log(
							`🔄 Complex event update received (${
								typeof e === "string" ? e : Object.keys(e)[0]
							}). Re-fetching event details...`,
						);
						dispatch(fetchEventThunk(eventId));
						return;
					}

					// Handle simple, discrete updates with dedicated actions.
					if ("checked_in" in e) {
						dispatch(eventCheckedIn({ eventId, at: e.checked_in.at }));
					} else if ("booked" in e) {
						dispatch(
							eventBooked({
								eventId,
								at: e.booked.at,
								userId: e.booked.user_id,
							}),
						);
					}
				} else if ("SessionUpdate" in data) {
					const sessionUpdate = data.SessionUpdate;
					if ("replay_ready" in sessionUpdate) {
						const replayData = sessionUpdate.replay_ready;
						const result = replayData.result as ResultDetailExtended;

						if (
							result?.field_goal_result === "score" &&
							replayData.replay_url &&
							result?.team_id != null
						) {
							dispatch(
								kioskQueuePlayback({
									session_idx: replayData.session_idx,
									result_idx: replayData.result_idx,
									replay_url: replayData.replay_url,
									result: result,
								}),
							);
						}
						dispatch(
							addSessionResult({
								eventId,
								sessionIdx: replayData.session_idx,
								resultIdx: replayData.result_idx,
								result: result,
							}),
						);
					} else if ("highlight_queued" in sessionUpdate) {
						const highlightData = sessionUpdate.highlight_queued;
						dispatch(
							addSessionHighlight({
								eventId,
								sessionIdx: highlightData.session_idx,
								highlight: {
									name: highlightData.title,
									video_url: "",
								},
							}),
						);
					} else if ("highlight_ready" in sessionUpdate) {
						const highlightData = sessionUpdate.highlight_ready;
						dispatch(
							updateSessionHighlight({
								eventId,
								sessionIdx: highlightData.session_idx,
								highlightIdx: highlightData.highlight_idx,
								updates: {
									video_url: highlightData.playback_url,
								},
							}),
						);
					} else if ("stream_ready" in sessionUpdate) {
						const d = sessionUpdate.stream_ready;
						dispatch(
							sessionStreamReady({
								eventId,
								sessionIdx: d.session_idx,
								streamUrl: d.stream_url,
							}),
						);
					} else if ("sharelink_ready" in sessionUpdate) {
						const d = sessionUpdate.sharelink_ready;
						dispatch(
							sessionSharelinkReady({
								eventId,
								sessionIdx: d.session_idx,
								url: d.url,
							}),
						);
					} else if ("processing_done" in sessionUpdate) {
						const d = sessionUpdate.processing_done;
						dispatch(
							sessionProcessingDone({ eventId, sessionIdx: d.session_idx }),
						);
					} else if ("session_archived" in sessionUpdate) {
						const d = sessionUpdate.session_archived;
						dispatch(sessionArchived({ eventId, sessionIdx: d.session_idx }));
					}
				}
			} catch (error) {
				console.error("Error parsing SSE message:", error, event.data);
			}
		},
	});
};

export const connectSseThunk = createAsyncThunk<void, string, ThunkConfig>(
	EventActionTypes.CONNECT_SSE,
	initSse,
);

export const fetchAgentsThunk = createAsyncThunk<
	components["schemas"]["AgentStatus"][]
>("Events/FETCH_AGENTS", async (_, { rejectWithValue }) => {
	try {
		const agents = await get_agents();
		return agents;
	} catch (error: any) {
		return rejectWithValue(error.message || "Failed to fetch agents");
	}
});

export const getAppendCodeThunk = createAsyncThunk(
	"Events/GET_APPEND_CODE",
	async (eventId: string) => {
		const data = await create_append_sessions_claim(eventId);
		return { eventId, token: data };
	},
);

export const getCheckInCodeThunk = createAsyncThunk(
	"Events/GET_CHECKIN_CODE",
	async (eventId: string) => {
		const data = await create_checkin_claim(eventId);
		return { eventId, token: data };
	},
);
